import React from 'react';
import { Button, IconButton, MenuItem, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// components
import GridPageAbstract from '../abstract/GridPageAbstract';
import WalletImportDialog from '../dialogs/WalletImportDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import Iconify from '../../components/iconify';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { formatApiErrors, hasError } from '../../utils/formatError';
import { requisitesProblems, templateFor } from '../../utils/walletRequisites';
import i18n from '../../i18n';

// The export's own cap (contract: 422 above 50,000). Sort and paging are
// ignored by the endpoint; this paging is sent only so that, were it ever
// honoured, it would still cover every row the export allows.
const CSV_EXPORT_MAX_ROWS = 50000;

// Must match action-permissions.yml in x-backend and ActionPermissions exactly.
// Switching a wallet on and off is its own permission, not part of editing it: a
// role may be given either without the other.
const TOGGLE_ACTION = 'wallet-toggle';

export default class WalletsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicWallets';
        this.Server = this.props.context.getState().server.publicWallets;
        this.MenuDisplayType = 'menu';

        const defaultHead = [
            { id: 'uuid', label: 'uuid', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            {
                id: 'gatewayId', required: true,
                label: 'gateway',
                alignRight: false,
                cell: 'Select',
                from: 'publicGateways',
                object: 'gateway',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'merchantTerminalId',
                label: 'merchantTerminal',
                alignRight: false,
                cell: 'Select',
                from: 'publicMerchantTerminals',
                object: 'merchantTerminal',
                show: 'name',
                displayGrid: true
            },
            // The fields of the gateway's requisites template when it names one
            // (the cabinet then refuses anything else), free-form otherwise, with
            // the templates' key names as preset chips. Save waits for the
            // template's checks, the same the cabinet makes.
            {
                id: 'receiverInfo',
                label: 'receiverInfo',
                required: true,
                alignRight: false,
                cell: 'WalletRequisites',
                displayGrid: true,
                helperText: 'walletReceiverInfoJson',
                validate: (value, values) => this.requisitesProblem(value, values)
            },
            // From public.wallet_balances, in the gateway's currency: the wallet's
            // takings, which core checks a payout against — available is received,
            // less paid out and held, plus what Wallet transfers moved to the card,
            // less what they moved off it. A wallet has no balance of its own: only
            // its gateway has one. Computed per page, so neither sortable nor
            // filterable, and never sent back.
            ...['available', 'received', 'paidOut', 'held', 'transferredIn', 'transferredOut'].map((key) => ({
                id: `balance.${key}`,
                label: `walletBalance.${key}`,
                alignRight: true,
                cell: 'Amount',
                object: 'balance',
                nestedObject: key,
                currencyObject: 'balance',
                displayGrid: true,
                disableEditor: true,
                disableSort: true
            })),
            {
                id: 'isActive',
                label: 'isActive',
                alignRight: false,
                cell: 'Enable',
                displayGrid: true,
                // On edit, isActive is the wallet-toggle action's, not update's:
                // without it the box is locked and goes back as it was — the API
                // refuses a change. A function, because the actions load after
                // this head is built.
                lockedOnEdit: () => !this.allowedAction(TOGGLE_ACTION)
            },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            csvImportOpen: false,
            csvExporting: false,
            toggleWallet: null
        };
    }

    async componentDidMount() {
        this.loadRequisitesTemplates();
        await super.componentDidMount();
    }

    /**
     * The requisites templates, once per session, into the store, where the
     * wallet editor and the CSV import read them. Without them both are
     * free-form, and the cabinet still checks what is saved.
     */
    loadRequisitesTemplates = async () => {
        const { context } = this.props;
        if (context.getState().walletRequisitesTemplates) return;
        try {
            const result = await this.Server.requisitesTemplates();
            if (!hasError(result) && Array.isArray(result.data)) {
                context.update({ walletRequisitesTemplates: result.data });
            }
        } catch (error) {
            console.error('Wallet requisites templates could not be loaded:', error);
        }
    };

    /** The receiverInfo rule: true, or why the gateway's template refuses it. */
    requisitesProblem = (value, values) => {
        const { controllersData, walletRequisitesTemplates } = this.props.context.getState();
        const template = templateFor(values?.gatewayId, controllersData?.publicGateways, walletRequisitesTemplates);
        // Absent and unparsed values are the required and Json rules' to report,
        // and an archived wallet is not checked until it is brought back.
        if (!template || value === null || value === undefined || typeof value === 'string' || values?.isArchived) {
            return true;
        }
        const problems = requisitesProblems(template, value);
        if (problems.extra.length) {
            return i18n.t('walletRequisites.extraKeys', { keys: problems.extra.join(', ') });
        }
        return problems.ok || i18n.t('walletRequisites.errors.fixFields');
    };

    renderCustomMenuItems(id) {
        const row = this.rowById(id);
        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0, so a bare element does not register.
        if (!row || !this.allowedAction(TOGGLE_ACTION)) return [];

        return [
            <MenuItem key={`${id}-toggle`} onClick={this.openToggle}>
                <Iconify
                    icon={row.isActive ? 'eva:slash-outline' : 'eva:checkmark-circle-2-outline'}
                    sx={{ mr: 2 }}
                />
                {i18n.t(row.isActive ? 'buttons.disableWallet' : 'buttons.enableWallet')}
            </MenuItem>
        ];
    }

    openToggle = () => {
        // Read before closing the menu, which nulls the row it was opened on.
        const row = this.getMenuClickItem();
        this.handleCloseMenu();
        this.setState({ toggleWallet: row });
    };

    closeToggle = () => {
        this.setState({ toggleWallet: null });
    };

    confirmToggle = async () => {
        const wallet = this.state.toggleWallet;
        if (!wallet) return;

        const result = wallet.isActive
            ? await this.Server.disable(wallet.id)
            : await this.Server.enable(wallet.id);
        this.setState({ toggleWallet: null });
        if (hasError(result)) {
            this.showSnackbar(formatApiErrors(result).join('\n'), 'error');
        } else {
            this.showSnackbar(i18n.t(wallet.isActive ? 'text.walletDisabled' : 'text.walletEnabled'), 'success');
        }
        await this.retrieveData();
    };

    renderHeaderExtraActions(isMobile) {
        const canImport = this.allowedPermission('create');
        const canExport = this.allowedPermission('read');
        if (!canImport && !canExport) return null;

        const { csvExporting } = this.state;
        const importLabel = i18n.t('walletCsv.importButton');
        const exportLabel = i18n.t('walletCsv.exportButton');
        const importIcon = <Iconify icon="eva:upload-outline" />;
        const exportIcon = <Iconify icon="eva:download-outline" />;

        if (isMobile) {
            return (
                <>
                    {canImport && (
                        <Tooltip title={importLabel}>
                            <IconButton color="primary" onClick={this.openCsvImport}>
                                {importIcon}
                            </IconButton>
                        </Tooltip>
                    )}
                    {canExport && (
                        <Tooltip title={exportLabel}>
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={this.handleExportCsv}
                                    disabled={csvExporting}
                                >
                                    {exportIcon}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </>
            );
        }

        return (
            <>
                {canImport && (
                    <Button variant="outlined" startIcon={importIcon} onClick={this.openCsvImport}>
                        {importLabel}
                    </Button>
                )}
                {canExport && (
                    <LoadingButton
                        variant="outlined"
                        startIcon={exportIcon}
                        loadingPosition="start"
                        loading={csvExporting}
                        onClick={this.handleExportCsv}
                    >
                        {exportLabel}
                    </LoadingButton>
                )}
            </>
        );
    }

    showSnackbar(message, severity) {
        this.props.context.update({
            alertError: { message, type: 'snackbar', severity }
        });
    }

    /**
     * Exports exactly what the grid is filtered to — search, archive toggle, date
     * range and filter controls, via the same currentListFilters() the list
     * request uses — not just the page on screen.
     */
    handleExportCsv = async () => {
        if (this.state.csvExporting) return;
        // The header is a snapshot; re-installed so the button shows the spinner.
        await this.setStateAsync({ csvExporting: true });
        this.installHeaderActions();

        try {
            const { filter, prefilter } = this.currentListFilters();
            const result = await this.Server.exportCsv({
                filter,
                prefilter,
                sort: this.currentListSort(),
                paging: { page: 0, pageSize: CSV_EXPORT_MAX_ROWS },
                includeTotalCount: false
            });
            if (hasError(result)) {
                this.showSnackbar(formatApiErrors(result).join('\n'), 'error');
            } else {
                this.showSnackbar(
                    i18n.t('walletCsv.exported', { fileName: result.data?.fileName }),
                    'success'
                );
            }
        } catch (error) {
            console.error('Wallet CSV export error:', error);
            this.showSnackbar(i18n.t('text.unexpectedError'), 'error');
        } finally {
            if (this._isMounted) {
                this.setState({ csvExporting: false }, () => this.installHeaderActions());
            }
        }
    };

    openCsvImport = () => {
        this.setState({ csvImportOpen: true });
    };

    closeCsvImport = (changed) => {
        this.setState({ csvImportOpen: false });
        if (changed) this.retrieveData();
    };

    renderCustom() {
        return (
            <>
                {this.renderCsvImport()}
                {this.renderToggleConfirm()}
            </>
        );
    }

    renderToggleConfirm() {
        const wallet = this.state.toggleWallet;
        if (!wallet) return null;

        // Mounted only while needed: ConfirmDialog reads its callbacks once, in
        // its constructor.
        return (
            <ConfirmDialog
                dialogOpen
                alertTitle={i18n.t('dialogs.confirmationTitle')}
                alertText={i18n.t(
                    wallet.isActive ? 'dialogs.walletDisableConfirm' : 'dialogs.walletEnableConfirm',
                    { uuid: wallet.uuid }
                )}
                onConfirm={this.confirmToggle}
                onCancel={this.closeToggle}
            />
        );
    }

    renderCsvImport() {
        if (!this.state.csvImportOpen) return null;

        // Read from the store at render time: this.state.controllersData is only
        // refreshed when permissions change, and lags the gateways loaded on mount.
        // Those are already the non-archived ones (updateContextControllersData).
        const { controllersData, server } = this.props.context.getState();

        return (
            <WalletImportDialog
                open
                onClose={this.closeCsvImport}
                server={this.Server}
                terminalsServer={server.publicMerchantTerminals}
                gateways={controllersData?.publicGateways || []}
                requisitesTemplates={this.props.context.getState().walletRequisitesTemplates || []}
                // A gateway user cannot read terminals, and the backend refuses
                // their import with one: no picker, rather than one that fails.
                canPickTerminal={this.allowedPermission('read', 'publicMerchantTerminals')}
            />
        );
    }
}
